import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { getAuthToken, logout } from '@/shared/auth/session';
import { isCognitoEnabled, isNativeCognitoEnabled } from '@/shared/auth/cognitoFeatureFlag';
import { cognitoClient } from '@/shared/auth/cognitoClient';
import { AppContext } from '@/models/AppContext';
import { PasswordUpdateError } from './http-error';

type CommissioningRequestConfig = InternalAxiosRequestConfig & {
  _retryAfterOidcRefresh?: boolean;
  _nativeSessionVersion?: number;
};

// Duplicated declaration of apiBaseUrl (also in axios.js) to avoid circular dependency.
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

/*
  Seperate axios instance for the commissioning to have different Bearer token from SolCentre.
  This separation is required until SolCentre and ComApp authentication are unified (rollout of Cognito auth).
*/
const commissioningApiClient = axios.create({ baseURL: apiBaseUrl });

function setupInterceptors(client: AxiosInstance): void {
  client.interceptors.request.use(
    async (config: CommissioningRequestConfig) => {
      if (isNativeCognitoEnabled) {
        if (
          config._nativeSessionVersion !== undefined &&
          config._nativeSessionVersion !== cognitoClient.sessionVersion()
        ) {
          throw new axios.CanceledError('Session changed');
        }
        config._nativeSessionVersion = cognitoClient.sessionVersion();
      }
      const token = await getAuthToken(AppContext.ComApp);
      if (
        isNativeCognitoEnabled &&
        config._nativeSessionVersion !== cognitoClient.sessionVersion()
      ) {
        throw new axios.CanceledError('Session changed');
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  client.interceptors.response.use(
    (response) => {
      if (
        isNativeCognitoEnabled &&
        response.config.url !== '/v2/auth/commissioner/reset-password' &&
        (response.config as CommissioningRequestConfig)._nativeSessionVersion !==
          cognitoClient.sessionVersion()
      ) {
        throw new axios.CanceledError('Session changed');
      }
      return response;
    },
    async (err) => {
      const cfg = err.config as CommissioningRequestConfig | undefined;
      if (
        isNativeCognitoEnabled &&
        (axios.isCancel(err) ||
          (cfg && cfg._nativeSessionVersion !== cognitoClient.sessionVersion()))
      ) {
        return Promise.reject(
          cfg?.url === '/v2/auth/commissioner/reset-password'
            ? new PasswordUpdateError(err)
            : axios.isCancel(err)
            ? err
            : new axios.CanceledError('Session changed'),
        );
      }

      if (cfg?.url === '/v2/auth/commissioner/reset-password') {
        return Promise.reject(new PasswordUpdateError(err));
      }

      if (isCognitoEnabled && cfg && !cfg._retryAfterOidcRefresh && err.response?.status === 401) {
        const freshToken = await getAuthToken(AppContext.ComApp);
        if (
          isNativeCognitoEnabled &&
          cfg._nativeSessionVersion !== cognitoClient.sessionVersion()
        ) {
          return Promise.reject(new axios.CanceledError('Session changed'));
        }
        if (freshToken) {
          cfg._retryAfterOidcRefresh = true;
          cfg.headers = cfg.headers ?? {};
          cfg.headers.Authorization = `Bearer ${freshToken}`;
          return client(cfg);
        }
      }

      if (err.request) {
        return Promise.reject(err);
      }

      if (err.response) {
        switch (err.response.status) {
          case 401:
            await logout(AppContext.ComApp);
            break;
          default:
            if (err.response.status >= 500) {
              // TODO
              // Sentry.captureMessage(`Error when requesting ${error.response.config.url}`, {
              //   tags: {
              //     url: error.response.config.url,
              //     message: error.response.data,
              //     status: error.response.status,
              //     method: error.response.config.method,
              //     payload: error.response.config.data,
              //   },
              // })
            }
            break;
        }
        return Promise.reject(
          err.response.data.message || `request failed with status ${err.response.status}`,
        );
      }
    },
  );
}

setupInterceptors(commissioningApiClient);

export default commissioningApiClient;
